import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import ProfilePicture from '@/components/ui/profile-picture';
import FollowButton from './FollowButton';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import {
  useGetFollowersQuery,
  useGetFollowingQuery,
} from '@/redux/follow/follow.api.slice';
import { selectCurrentUser, selectUserId } from '@/redux/auth/auth.slice';
import { IUser } from '@/redux/auth/auth';

const PAGE_LIMIT = 20;

interface FollowersModalProps {
  targetUserId: string;
  mode: 'followers' | 'following';
  count: number;
}

function UserListItem({
  user,
  currentUserId,
  onClose,
}: {
  user: IUser;
  currentUserId: string | null;
  onClose: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <Link
        to={`/${user.username}`}
        onClick={onClose}
        className="flex items-center gap-3 min-w-0"
      >
        <ProfilePicture
          src={user.profilePicture}
          alt={user.username}
          username={user.username}
          variant="badge"
        />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">@{user.username}</p>
          <p className="text-xs text-gray-500 truncate">
            {user.firstname} {user.lastname}
          </p>
        </div>
      </Link>
      {currentUserId && (
        <div className="shrink-0">
          <FollowButton
            targetUserId={user._id}
            isFollowing={user.isFollowing ?? false}
            isOwnProfile={currentUserId === user._id}
          />
        </div>
      )}
    </div>
  );
}

function FollowersList({
  targetUserId,
  currentUserId,
  onClose,
}: {
  targetUserId: string;
  currentUserId: string | null;
  onClose: () => void;
}) {
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState<IUser[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const {
    data: pageData,
    isLoading,
    isFetching,
    isError,
  } = useGetFollowersQuery({ targetUserId, page, limit: PAGE_LIMIT });

  useEffect(() => {
    setPage(1);
    setUsers([]);
    setHasMore(true);
  }, [targetUserId]);

  useEffect(() => {
    if (!pageData) return;
    setUsers((prev) => (page === 1 ? pageData : [...prev, ...pageData]));
    setHasMore(pageData.length === PAGE_LIMIT);
  }, [pageData, page]);

  const loadMore = useCallback(async () => {
    setPage((p) => p + 1);
  }, []);

  if (isLoading && page === 1)
    return <p className="text-sm text-gray-500 py-4 text-center">Loading...</p>;
  if (isError)
    return (
      <p className="text-sm text-red-500 py-4 text-center">
        Failed to load followers.
      </p>
    );
  if (!users.length && !isFetching)
    return (
      <p className="text-sm text-gray-500 py-4 text-center">
        No followers yet.
      </p>
    );

  return (
    <InfiniteScroll
      onLoadMore={loadMore}
      hasMore={hasMore}
      isLoading={isFetching}
    >
      <div className="divide-y">
        {users.map((user) => (
          <UserListItem
            key={user._id}
            user={user}
            currentUserId={currentUserId}
            onClose={onClose}
          />
        ))}
      </div>
    </InfiniteScroll>
  );
}

function FollowingList({
  targetUserId,
  currentUserId,
  onClose,
}: {
  targetUserId: string;
  currentUserId: string | null;
  onClose: () => void;
}) {
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState<IUser[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const {
    data: pageData,
    isLoading,
    isFetching,
    isError,
  } = useGetFollowingQuery({ targetUserId, page, limit: PAGE_LIMIT });

  useEffect(() => {
    setPage(1);
    setUsers([]);
    setHasMore(true);
  }, [targetUserId]);

  useEffect(() => {
    if (!pageData) return;
    setUsers((prev) => (page === 1 ? pageData : [...prev, ...pageData]));
    setHasMore(pageData.length === PAGE_LIMIT);
  }, [pageData, page]);

  const loadMore = useCallback(async () => {
    setPage((p) => p + 1);
  }, []);

  if (isLoading && page === 1)
    return <p className="text-sm text-gray-500 py-4 text-center">Loading...</p>;
  if (isError)
    return (
      <p className="text-sm text-red-500 py-4 text-center">
        Failed to load following.
      </p>
    );
  if (!users.length && !isFetching)
    return (
      <p className="text-sm text-gray-500 py-4 text-center">
        Not following anyone yet.
      </p>
    );

  return (
    <InfiniteScroll
      onLoadMore={loadMore}
      hasMore={hasMore}
      isLoading={isFetching}
    >
      <div className="divide-y">
        {users.map((user) => (
          <UserListItem
            key={user._id}
            user={user}
            currentUserId={currentUserId}
            onClose={onClose}
          />
        ))}
      </div>
    </InfiniteScroll>
  );
}

export const STAT_CELL =
  'flex w-full flex-col items-center px-3 py-2.5 sm:px-5 transition-colors hover:bg-green-50 focus-visible:outline-none focus-visible:shadow-focus';

export function StatValue({ count, label }: { count: number; label: string }) {
  return (
    <>
      <span className="font-display text-xl font-semibold leading-tight tabular-nums text-green-950">
        {count.toLocaleString()}
      </span>{' '}
      <span className="text-xs text-green-900/70">{label}</span>
    </>
  );
}

function FollowersModal({ targetUserId, mode, count }: FollowersModalProps) {
  const [open, setOpen] = useState(false);
  const currentUserId = useSelector(selectUserId);
  const { isAuthenticated } = useSelector(selectCurrentUser);

  const label = mode === 'followers' ? 'Followers' : 'Following';

  return (
    <div className="flex-1">
      {isAuthenticated ? (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button type="button" className={STAT_CELL}>
              <StatValue count={count} label={label} />
            </button>
          </DialogTrigger>
          <DialogContent className="max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{label}</DialogTitle>
            </DialogHeader>
            {mode === 'followers' ? (
              <FollowersList
                targetUserId={targetUserId}
                currentUserId={currentUserId}
                onClose={() => setOpen(false)}
              />
            ) : (
              <FollowingList
                targetUserId={targetUserId}
                currentUserId={currentUserId}
                onClose={() => setOpen(false)}
              />
            )}
          </DialogContent>
        </Dialog>
      ) : (
        <div className={STAT_CELL}>
          <StatValue count={count} label={label} />
        </div>
      )}
    </div>
  );
}

export default FollowersModal;
